export type DashboardPeriod = 'today' | '7d' | '30d' | 'month' | '90d' | 'custom' | 'all'
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = 'today'
export const dashboardPeriodOptions = [
  { value: 'today', label: 'Hoje' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: 'month', label: 'Este mês' },
  { value: 'custom', label: 'Personalizado' },
] satisfies ReadonlyArray<{ value: DashboardPeriod; label: string }>

const DAY_MS = 86_400_000
export type DashboardDateRange = { dateFrom?: string; dateTo?: string }

export function dashboardToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(now)
}

export function dashboardCustomRange(from: string, to: string): DashboardDateRange | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return undefined
  const start = Date.parse(`${from}T00:00:00-03:00`)
  const end = Date.parse(`${to}T00:00:00-03:00`) + DAY_MS
  if (
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    end <= start ||
    end - start > 366 * DAY_MS
  )
    return undefined
  if (dashboardToday(new Date(start)) !== from || dashboardToday(new Date(end - DAY_MS)) !== to)
    return undefined
  return { dateFrom: new Date(start).toISOString(), dateTo: new Date(end).toISOString() }
}

export function dashboardPeriodRange(
  period: DashboardPeriod,
  now = new Date(),
): DashboardDateRange {
  if (period === 'all' || period === 'custom') return {}
  const today = dashboardToday(now)
  if (period === 'today' || period === 'month') {
    const from = period === 'today' ? today : `${today.slice(0, 7)}-01`
    return { dateFrom: new Date(`${from}T00:00:00-03:00`).toISOString(), dateTo: now.toISOString() }
  }
  const days = { '7d': 7, '30d': 30, '90d': 90 }[period]
  return {
    dateFrom: new Date(now.getTime() - days * DAY_MS).toISOString(),
    dateTo: now.toISOString(),
  }
}

export function dashboardPeriodLabel(period: DashboardPeriod): string {
  return {
    today: 'hoje',
    '7d': 'últimos 7 dias',
    '30d': 'últimos 30 dias',
    month: 'este mês',
    '90d': 'últimos 90 dias',
    custom: 'período personalizado',
    all: 'todo o histórico',
  }[period]
}

export function adminListPeriodRange(
  period: DashboardPeriod,
  now = new Date(),
): DashboardDateRange {
  if (period === 'all' || period === 'custom') return {}
  const today = dashboardToday(now)
  const startOfDay = Date.parse(`${today}T00:00:00-03:00`)
  const days = { today: 1, '7d': 7, '30d': 30, '90d': 90, month: 1 }[period]
  const from =
    period === 'month'
      ? Date.parse(`${today.slice(0, 7)}-01T00:00:00-03:00`)
      : startOfDay - (days - 1) * DAY_MS
  return {
    dateFrom: new Date(from).toISOString(),
    dateTo: new Date(startOfDay + DAY_MS).toISOString(),
  }
}

export function dashboardRangeLabel(range: DashboardDateRange): string {
  if (!range.dateFrom || !range.dateTo) return 'Todo o histórico'
  const format = (value: string) =>
    new Date(value).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      dateStyle: 'short',
      timeStyle: 'short',
    })
  return `${format(range.dateFrom)} a ${format(range.dateTo)} · Brasília`
}
